import {
  CirclePause,
  CirclePlay,
  CircleStop,
  LockKeyhole,
  Pause,
  Play,
  Square,
} from "lucide-react";
import React from "react";
import { useDispatch } from "react-redux";

import {
  IgButton,
  IgIconButton,
  IgSplitButton,
  IgTooltip,
} from "@intelligems/ig-theme";
import { capitalizeFirstLetter } from "@intelligems/ig-utils";
import { useTheme } from "@mui/material";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { UpgradePlanText } from "@src/components/LimitAccess/access-text";
import { Experience } from "@src/store/slices/experiences/types";
import { setDialog } from "@src/store/slices/global";

export interface ActionExperienceButtonProps {
  isLoading: boolean;
  disabled?: boolean;
  onClick: (experience: Experience) => void;
  experience: Experience;
  action: "start" | "end" | "pause";
  renderLocation: "overview" | "experience";
  hasSchedule: boolean;
  onDisableClick?: () => void;
  onPauseExp?: (experience: Experience) => void;
}

export const ActionExperienceButton = ({
  isLoading,
  disabled,
  onClick,
  experience,
  action,
  onDisableClick,
  renderLocation,
  hasSchedule,
  onPauseExp,
}: ActionExperienceButtonProps) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const enableScheduleTests = useConfiguration<boolean>(
    "enable_schedule_tests"
  )!.value;

  if (renderLocation === "experience") {
    if (experience.status === "pending") {
      return (
        <IgSplitButton
          startIcon={
            <Play
              size={16}
              style={{ marginRight: "4px" }}
              color={theme.igIconColors.dark}
            />
          }
          disabled={disabled}
          loading={isLoading}
          size={"small"}
          sx={{
            width: "85px",
            color: "white",
            fontSize: "14px",
            fontWeight: 600,
          }}
          variant={"contained"}
          onClick={() => {
            if (disabled) {
              if (onDisableClick) {
                onDisableClick();
              }
            } else {
              onClick(experience);
            }
          }}
          color={"primary"}
          menuOptions={[
            {
              key: "Start",
              hidden: true,
              action: () => {
                onClick(experience);
              },
            },
            {
              key: "Start Test",
              action: () => {
                onClick(experience);
              },
            },
            {
              key: hasSchedule ? "Edit or Cancel Schedule" : "Schedule Test",
              action: () => {
                dispatch(
                  setDialog({
                    dialog: "scheduleTest",
                    open: true,
                    props: {
                      experience,
                    },
                  })
                );
              },
            },
          ]}
        />
      );
    } else if (experience.status === "started") {
      return (
        <IgSplitButton
          startIcon={
            <Square
              size={16}
              style={{ marginRight: "4px" }}
              color={theme.igIconColors.dark}
            />
          }
          disabled={disabled}
          loading={isLoading}
          size={"small"}
          sx={{
            width: "85px",
            color: "white",
            fontSize: "14px",
            fontWeight: 600,
          }}
          variant={"contained"}
          color={"primary"}
          onClick={() => {
            if (disabled) {
              if (onDisableClick) {
                onDisableClick();
              }
            } else {
              onClick(experience);
            }
          }}
          menuOptions={[
            {
              key: "End",
              hidden: true,
              action: () => onClick(experience),
            },
            {
              key: "End Test",
              action: () => onClick(experience),
            },
            {
              key: "Pause Test",
              action: () => onPauseExp?.(experience),
            },
            {
              key: hasSchedule ? "Edit or Cancel Schedule" : "Schedule Test",
              disabledMessage: enableScheduleTests ? "" : UpgradePlanText,
              action: () => {
                dispatch(
                  setDialog({
                    dialog: "scheduleTest",
                    open: true,
                    props: {
                      experience,
                    },
                  })
                );
              },
            },
          ]}
        />
      );
    }

    return (
      <>
        {disabled ? (
          <IgButton
            startIcon={
              action === "start" ? (
                <Play size={16} color={theme.igIconColors.dark} />
              ) : action === "pause" ? (
                <Pause size={16} color={theme.igIconColors.dark} />
              ) : (
                <Square size={16} color={theme.igIconColors.dark} />
              )
            }
            size={"small"}
            color={"primary"}
            variant={"contained"}
            loading={isLoading}
            onClick={() => {
              if (onDisableClick) {
                onDisableClick();
              }
            }}
          >
            {capitalizeFirstLetter(action)}
          </IgButton>
        ) : (
          <IgButton
            size={"small"}
            loading={isLoading}
            variant={action === "end" ? "outlined" : "contained"}
            disabled={disabled}
            sx={{
              minWidth: "100px",
              ...(action !== "end" && { color: "white" }),
              fontSize: "14px",
              fontWeight: 600,
            }}
            color={"primary"}
            onClick={() => {
              onClick(experience);
            }}
            startIcon={
              action === "start" ? (
                <Play size={16} color={theme.igIconColors.dark} />
              ) : action === "pause" ? (
                <Pause size={16} color={theme.igIconColors.dark} />
              ) : (
                <Square size={16} color={theme.igIconColors.light} />
              )
            }
          >
            {action === "start" ? "Resume" : capitalizeFirstLetter(action)}
          </IgButton>
        )}
      </>
    );
  }

  return (
    <IgTooltip
      title={
        disabled
          ? ""
          : action === "start"
            ? "Start"
            : action === "pause"
              ? "Pause"
              : "Stop"
      }
    >
      {disabled ? (
        <IgIconButton
          size={"small"}
          loading={isLoading}
          color={"primary"}
          onClick={() => {
            if (onDisableClick) {
              onDisableClick();
            }
          }}
        >
          <LockKeyhole size={16} />
        </IgIconButton>
      ) : (
        <IgIconButton
          size={"small"}
          loading={isLoading}
          disabled={disabled}
          onClick={() => {
            onClick(experience);
          }}
        >
          {action === "start" ? (
            <CirclePlay
              color={theme.palette.text.interactive.neutral.disabled}
            />
          ) : action === "pause" ? (
            <CirclePause
              color={theme.palette.text.interactive.neutral.disabled}
            />
          ) : (
            <CircleStop
              color={theme.palette.text.interactive.neutral.disabled}
            />
          )}
        </IgIconButton>
      )}
    </IgTooltip>
  );
};
